import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { supplierService } from "../services/SupplierService";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function ManageSuppliers() {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [allRegionsData, setAllRegionsData] = useState([]);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [filteredSuppliersCount, setFilteredSuppliersCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    fetchSuppliers();
  }, [currentPage, search, status]);

  useEffect(() => {
    if (country === "all") {
      const uniqueRegions = [...new Set(allRegionsData.map(item => item.region))].sort();
      setAvailableRegions(uniqueRegions);
    } else {
      const countryRegions = allRegionsData
        .filter(item => item.country && item.country.toLowerCase() === country.toLowerCase())
        .map(item => item.region);
      const uniqueRegions = [...new Set(countryRegions)].sort();
      setAvailableRegions(uniqueRegions);
    }
    
    setRegion("all");
  }, [country, allRegionsData]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(status !== 'all' && { status }),
        ...(search && { search })
      };

      const response = await supplierService.getAllSuppliers(params);
      
      if (response.suppliers && Array.isArray(response.suppliers)) {
        const supplierList = response.suppliers;
        setSuppliers(supplierList);
        
        // Extract unique countries from the response
        const countries = [...new Set(supplierList
          .map(supplier => supplier.country)
          .filter(country => country && country.trim() !== '')
        )].sort();
        
        setAvailableCountries(countries);

        // Store all country-region pairs for filtering
        const regionsData = supplierList
          .map(supplier => ({
            country: supplier.country,
            region: supplier.region
          }))
          .filter(item => item.region && item.region.trim() !== '' && item.country && item.country.trim() !== '');
        
        setAllRegionsData(regionsData);

        // Set initial available regions
        const uniqueRegions = [...new Set(regionsData.map(item => item.region))].sort();
        setAvailableRegions(uniqueRegions);
        
        // Set total suppliers from pagination
        if (response.pagination && response.pagination.total) {
          setTotalSuppliers(response.pagination.total);
          if (search || status !== 'all') {
            setFilteredSuppliersCount(response.pagination.total);
          } else {
            setFilteredSuppliersCount(response.pagination.total);
          }
        }
      } else {
        setSuppliers([]);
        setAvailableCountries([]);
        setAvailableRegions([]);
        setAllRegionsData([]);
        setTotalSuppliers(0);
        setFilteredSuppliersCount(0);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSnackMessage('Failed to load suppliers');
      setSnackOpen(true);
      setSuppliers([]);
      setAvailableCountries([]);
      setAvailableRegions([]);
      setAllRegionsData([]);
      setTotalSuppliers(0);
      setFilteredSuppliersCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-supplier-account");
  };

  // Format API data
  const formatSuppliers = (apiSuppliers) => {
    return apiSuppliers.map(supplier => ({
      id: supplier.supplier_id,
      fullName: supplier.full_name,
      firstName: supplier.first_name,
      lastName: supplier.last_name,
      email: supplier.email,
      phone: supplier.phone_number,
      retailType: supplier.retail_info?.retail_type || supplier.retail_type || 'Not specified',
      retailSubType: supplier.retail_info?.retail_sub_type || supplier.retail_sub_type || 'Not specified',
      retailName: supplier.retail_info?.retail_name || supplier.retail_name || 'Not specified',
      address: {
        line1: supplier.address_line1 || '',
        line2: supplier.address_line2 || '',
        line3: supplier.address_line3 || '',
        city: supplier.city || '',
        county: supplier.region || 'N/A',
        country: supplier.country || 'N/A',
        postcode: supplier.postcode || ''
      },
      status: supplier.current_status ? supplier.current_status.charAt(0).toUpperCase() + supplier.current_status.slice(1) : 'Awaiting',
      supplier_status: supplier.status || 'active',
      notes: supplier.status_notes || '',
      photo_url: supplier.photo_url,
      has_keys_generated: supplier.has_keys_generated || false,
      key_algorithm: supplier.key_algorithm,
      keys_generated_at: supplier.keys_generated_at,
      verification_status: supplier.verification_status,
      products_summary: supplier.products_summary,
      created_at: supplier.created_at,
      updated_at: supplier.updated_at,
      ownerCountry: supplier.country || 'N/A',
      ...supplier
    }));
  };

  const handleRowClick = (supplier) => {
    navigate("/supplier-details", { state: { supplier } });
  };

  const formattedSuppliers = suppliers.length > 0 ? formatSuppliers(suppliers) : [];
  
  const filteredSuppliers = formattedSuppliers.filter((supplier) => {
    const countryMatch = country === "all" || 
      (supplier.ownerCountry && supplier.ownerCountry.toLowerCase() === country.toLowerCase());
    
    const regionMatch = region === "all" || 
      (supplier.address.county && supplier.address.county.toLowerCase() === region.toLowerCase());

    return countryMatch && regionMatch;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalSuppliers / itemsPerPage);

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Suppliers</h2>
            <button className="action-btn" onClick={goToCreateAccount}>+ Add New Supplier</button>
          </div>

          {/* Supplier Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Suppliers:</span>
              <span className="stat-value">{totalSuppliers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Suppliers:</span>
              <span className="stat-value">{filteredSuppliers.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {filteredSuppliers.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="filter-select">
                <option value="all">All Countries</option>
                {availableCountries.map((countryName, index) => (
                  <option key={index} value={countryName.toLowerCase()}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by region:</label>
              <select 
                value={region} 
                onChange={(e) => setRegion(e.target.value)} 
                className="filter-select"
                disabled={availableRegions.length === 0}
              >
                <option value="all">
                  {availableRegions.length === 0 ? "No regions available" : "All Regions"}
                </option>
                {availableRegions.map((regionName, index) => (
                  <option key={index} value={regionName.toLowerCase()}>
                    {regionName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by status:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name, email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>ID</th>
                <th style={{ width: "150px" }}>Supplier Name</th>
                <th style={{ width: "150px" }}>Retail/Shop Name</th>
                <th style={{ width: "130px" }}>Telephone</th>
                <th style={{ width: "200px" }}>Email</th>
                <th style={{ width: "130px" }}>Retail Type</th>
                <th style={{ width: "120px" }}>Country</th>
                <th style={{ width: "100px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Suppliers...</div>
                  </td>
                </tr>
              ) : filteredSuppliers.length > 0 ? (
                filteredSuppliers.map((supplier, i) => (
                  <tr 
                    key={supplier.id || i}
                    onClick={() => handleRowClick(supplier)}
                  >
                    <td>{supplier.id}</td>
                    <td>{supplier.fullName}</td>
                    <td>{supplier.retailName}</td>
                    <td>{supplier.phone}</td>
                    <td>{supplier.email}</td>
                    <td>{supplier.retailType}</td>
                    <td>{supplier.ownerCountry}</td>
                    <td>
                      <span className={`status ${supplier.current_status.toLowerCase()}`}>
                        {supplier.current_status.toUpperCase().charAt(0) + supplier.current_status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    {filteredSuppliersCount === 0 && totalSuppliers > 0 
                      ? "No suppliers match your filters" 
                      : "No suppliers found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && totalSuppliers > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          severity="success"
          icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
          sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '200px',
            }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageSuppliers;